"use client";

import type React from "react";
import { useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { MdAccessTime, MdNotifications, MdSettings, MdTrendingUp } from "react-icons/md";

// Custom Components
const Card = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const Button = ({
  children,
  variant = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode;
  variant?: "default" | "outline" | "secondary";
  className?: string;
  onClick?: () => void;
  [key: string]: any;
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none h-10 px-4 py-2";

  const variants = {
    default: "bg-green-600 text-white hover:bg-green-700",
    outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Select = ({
  children,
  className,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  children: React.ReactNode;
}) => (
  <select
    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </select>
);

const Switch = ({
  checked,
  onCheckedChange,
  className = "",
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}) => {
  return (
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-green-600" : "bg-gray-200"
      } ${className}`}
      onClick={() => onCheckedChange(!checked)}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
};

export const SystemSettings = () => {
  // Delivery Configuration State
  const [maxDeliveryRadius, setMaxDeliveryRadius] = useState("15");
  const [deliveryTimeWindow, setDeliveryTimeWindow] = useState("25");
  const [orderAcceptanceTimeout, setOrderAcceptanceTimeout] = useState("120");
  const [maxOrdersPerRider, setMaxOrdersPerRider] = useState("3 Orders");

  // Earnings & Bonuses State
  const [baseDeliveryRate, setBaseDeliveryRate] = useState("15");
  const [distanceRate, setDistanceRate] = useState("2.0");
  const [fastDeliveryBonus, setFastDeliveryBonus] = useState("125");
  const [weekendBonusMultiplier, setWeekendBonusMultiplier] = useState("1.2");

  // Notification Settings State
  const [pushNotifications, setPushNotifications] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [smsNotifications, setSmsNotifications] = useState(true);
  const [riderAppUpdates, setRiderAppUpdates] = useState(true);
  const [maintenanceAlert, setMaintenanceAlert] = useState(true);

  // System Configuration State
  const [autoAssignOrders, setAutoAssignOrders] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const [realTimeTracking, setRealTimeTracking] = useState(true);
  const [riderAppUpdatesSystem, setRiderAppUpdatesSystem] = useState(true);
  const [dataRetention, setDataRetention] = useState("1 Year");
  const [backupFrequency, setBackupFrequency] = useState("Daily");

  // Performance Thresholds State
  const [minAcceptanceRate, setMinAcceptanceRate] = useState("80");
  const [minCompletionRate, setMinCompletionRate] = useState("90");
  const [maxDeliveryTime, setMaxDeliveryTime] = useState("35");
  const [minCustomerRating, setMinCustomerRating] = useState("4.0");

  const handleSaveChanges = () => {
    // Handle save logic here
    console.log("Saving changes...");
  };

  const handleResetChanges = () => {
    // Handle reset logic here
    console.log("Resetting changes...");
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <header className="mb-6 h-16 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-4">
          <BiArrowBack 
            className="w-6 h-6 text-gray-600 cursor-pointer" 
            onClick={() => window.history.back()} 
          />
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Logistics</span>
              <span>›</span>
              <span>System Settings</span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              System Settings
            </h2>
            <p className="text-sm text-gray-500">
              Configure delivery operations and system parameters
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleResetChanges}>
            Reset Changes
          </Button>
          <Button onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Delivery Configuration */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MdAccessTime className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Delivery Configuration
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Delivery Radius (km)
              </label>
              <Input
                type="number"
                value={maxDeliveryRadius}
                onChange={(e) => setMaxDeliveryRadius(e.target.value)}
                placeholder="15"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Time Window (minutes)
              </label>
              <Input
                type="number"
                value={deliveryTimeWindow}
                onChange={(e) => setDeliveryTimeWindow(e.target.value)}
                placeholder="25"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Acceptance Timeout (seconds)
              </label>
              <Input
                type="number"
                value={orderAcceptanceTimeout}
                onChange={(e) => setOrderAcceptanceTimeout(e.target.value)}
                placeholder="120"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Orders Per Rider
              </label>
              <Select
                value={maxOrdersPerRider}
                onChange={(e) => setMaxOrdersPerRider(e.target.value)}
              >
                <option>1 Order</option>
                <option>2 Orders</option>
                <option>3 Orders</option>
                <option>4 Orders</option>
                <option>5 Orders</option>
              </Select>
            </div>
          </div>
        </Card>

        {/* Earnings & Bonuses */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <MdTrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Earnings & Bonuses
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base Delivery Rate (₦)
              </label>
              <Input
                type="number"
                value={baseDeliveryRate}
                onChange={(e) => setBaseDeliveryRate(e.target.value)}
                placeholder="15"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Distance Rate (₦/km)
              </label>
              <Input
                type="number"
                step="0.1"
                value={distanceRate}
                onChange={(e) => setDistanceRate(e.target.value)}
                placeholder="2.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fast Delivery Bonus (₦)
              </label>
              <Input
                type="number"
                value={fastDeliveryBonus}
                onChange={(e) => setFastDeliveryBonus(e.target.value)}
                placeholder="125"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Weekend Bonus Multiplier
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.1"
                  value={weekendBonusMultiplier}
                  onChange={(e) => setWeekendBonusMultiplier(e.target.value)}
                  placeholder="1.2"
                  className="flex-1"
                />
                <span className="text-sm text-gray-500 min-w-fit">1.2</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Notification Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <MdNotifications className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Notification Settings
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Push Notifications</p>
                <p className="text-sm text-gray-500">Send Push Notification to Riders</p>
              </div>
              <Switch
                checked={pushNotifications}
                onCheckedChange={setPushNotifications}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">E-mail Notifications</p>
                <p className="text-sm text-gray-500">Send important updates via email</p>
              </div>
              <Switch
                checked={emailNotifications}
                onCheckedChange={setEmailNotifications}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">SMS Notifications</p>
                <p className="text-sm text-gray-500">Send critical alerts via SMS</p>
              </div>
              <Switch
                checked={smsNotifications}
                onCheckedChange={setSmsNotifications}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Rider App Updates</p>
                <p className="text-sm text-gray-500">Notify riders about app updates</p>
              </div>
              <Switch
                checked={riderAppUpdates}
                onCheckedChange={setRiderAppUpdates}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Maintenance Alert</p>
                <p className="text-sm text-gray-500">Alert riders about system maintenance</p>
              </div>
              <Switch
                checked={maintenanceAlert}
                onCheckedChange={setMaintenanceAlert}
              />
            </div>
          </div>
        </Card>

        {/* System Configuration */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MdSettings className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              System Configuration
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Auto Assign Orders</p>
                <p className="text-sm text-gray-500">Automatically assign orders to riders</p>
              </div>
              <Switch
                checked={autoAssignOrders}
                onCheckedChange={setAutoAssignOrders}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Manual Override</p>
                <p className="text-sm text-gray-500">Allow manual order assignment</p>
              </div>
              <Switch
                checked={manualOverride}
                onCheckedChange={setManualOverride}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Real-time Tracking</p>
                <p className="text-sm text-gray-500">Track rider locations in real-time</p>
              </div>
              <Switch
                checked={realTimeTracking}
                onCheckedChange={setRealTimeTracking}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Rider App Updates</p>
                <p className="text-sm text-gray-500">Notify riders about app updates</p>
              </div>
              <Switch
                checked={riderAppUpdatesSystem}
                onCheckedChange={setRiderAppUpdatesSystem}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Retention (days)
              </label>
              <Select
                value={dataRetention}
                onChange={(e) => setDataRetention(e.target.value)}
              >
                <option>30 Days</option>
                <option>90 Days</option>
                <option>180 Days</option>
                <option>1 Year</option>
                <option>2 Years</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Backup Frequency
              </label>
              <Select
                value={backupFrequency}
                onChange={(e) => setBackupFrequency(e.target.value)}
              >
                <option>Hourly</option>
                <option>Daily</option>
                <option>Weekly</option>
                <option>Monthly</option>
              </Select>
            </div>
          </div>
        </Card>
      </div>

      {/* Performance Thresholds */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <MdTrendingUp className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            Performance Thresholds
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Acceptance Rate (%)
            </label>
            <Input
              type="number"
              value={minAcceptanceRate}
              onChange={(e) => setMinAcceptanceRate(e.target.value)}
              placeholder="80"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Completion Rate (%)
            </label>
            <Input
              type="number"
              value={minCompletionRate}
              onChange={(e) => setMinCompletionRate(e.target.value)}
              placeholder="90"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Delivery Time (minutes)
            </label>
            <Input
              type="number"
              value={maxDeliveryTime}
              onChange={(e) => setMaxDeliveryTime(e.target.value)}
              placeholder="35"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Customer Rating
            </label>
            <Input
              type="number"
              step="0.1"
              value={minCustomerRating}
              onChange={(e) => setMinCustomerRating(e.target.value)}
              placeholder="4.0"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};